import { useCallback, useEffect, useState } from "react";
import { ContactSheet } from "../components/ContactSheet";
import { SettingsDrawer } from "../components/SettingsDrawer";
import { UploadDropzone } from "../components/UploadDropzone";
import {
  api,
  ApiError,
  type ConnectionStatus,
  type GalleryPage,
  type GalleryStats,
  type Settings,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatBytes } from "../lib/format";
import { useCloudreveCallback } from "../lib/useCloudreveCallback";

const PAGE_SIZE = 24;

export function DashboardPage() {
  const { username, signOut } = useAuth();

  const [connection, setConnection] = useState<ConnectionStatus | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [gallery, setGallery] = useState<GalleryPage | null>(null);
  const [stats, setStats] = useState<GalleryStats | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoadingGallery, setIsLoadingGallery] = useState(true);

  // Typing in the search box should not fire a request per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const loadConnection = useCallback(async () => {
    try {
      setConnection(await api.connection.status());
    } catch (cause) {
      if (!(cause instanceof ApiError && cause.isAuthFailure)) {
        setConnection({ connected: false, refresh_valid: false });
      }
    }
  }, []);

  const loadGallery = useCallback(async () => {
    setIsLoadingGallery(true);
    try {
      const [result, counters] = await Promise.all([
        api.gallery.list(page, PAGE_SIZE, debouncedSearch),
        api.gallery.stats(),
      ]);
      setGallery(result);
      setStats(counters);
    } catch (cause) {
      if (!(cause instanceof ApiError && cause.isAuthFailure)) {
        setGallery({ items: [], total: 0, page: 1, page_size: PAGE_SIZE });
      }
    } finally {
      setIsLoadingGallery(false);
    }
  }, [page, debouncedSearch]);

  // Returning from Cloudreve lands here, not in the settings panel.
  useCloudreveCallback(loadConnection);

  useEffect(() => {
    void loadConnection();
    api.settings.get().then(setSettings).catch(() => setSettings(null));
  }, [loadConnection]);

  useEffect(() => {
    void loadGallery();
  }, [loadGallery]);

  // The backend refreshes tokens on its own; this only keeps the panel honest
  // about what it is showing.
  useEffect(() => {
    const timer = window.setInterval(() => void loadConnection(), 60_000);
    return () => window.clearInterval(timer);
  }, [loadConnection]);

  const canUpload = connection?.connected === true && connection.refresh_valid;
  const connectionState = !connection?.connected
    ? "down"
    : connection.refresh_valid
      ? "live"
      : "warn";

  return (
    <div className="shell">
      <header className="topbar">
        <span className="topbar__mark">Image desk</span>

        <div className="topbar__stats">
          <div className="topbar__stat">
            <span className="label">Filed</span>
            <b>{stats?.total_uploads ?? "—"}</b>
          </div>
          <div className="topbar__stat">
            <span className="label">Last 24h</span>
            <b>{stats?.uploads_today ?? "—"}</b>
          </div>
          <div className="topbar__stat">
            <span className="label">Stored</span>
            <b>{stats ? formatBytes(stats.total_bytes) : "—"}</b>
          </div>
        </div>

        {/* The dot doubles as the connection indicator, so a broken link is
            visible without opening anything. */}
        <button
          className="btn topbar__settings"
          type="button"
          onClick={() => setIsDrawerOpen(true)}
          aria-haspopup="dialog"
        >
          <span className="topbar__dot" data-state={connectionState} aria-hidden="true" />
          Settings
        </button>

        <button className="btn btn--quiet" type="button" onClick={signOut}>
          Sign out {username ? `(${username})` : ""}
        </button>
      </header>

      <main className="workspace">
        <UploadDropzone
          settings={settings}
          disabled={!canUpload}
          onUploaded={() => {
            setPage(1);
            void loadGallery();
          }}
        />

        {!canUpload && (
          <div className="notice notice--info">
            Cloudreve is not connected, so uploads are off.{" "}
            <button className="linkbtn" type="button" onClick={() => setIsDrawerOpen(true)}>
              Open settings to authorise it.
            </button>
          </div>
        )}

        <ContactSheet
          page={gallery}
          isLoading={isLoadingGallery}
          search={search}
          onSearchChange={setSearch}
          onPageChange={setPage}
          onChanged={loadGallery}
        />
      </main>

      <SettingsDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        connection={connection}
        settings={settings}
        onConnectionChanged={loadConnection}
        onSettingsSaved={setSettings}
      />
    </div>
  );
}
